"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useUserDefaultAddress = exports.useAddressOnCart = exports.useAddressForm = void 0;
/*
Copyright (C) 2009 - 2021 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
const react_1 = require("react");
const lodash_1 = require("lodash");
const payment_js_1 = require("@broadleaf/payment-js");
const use_is_saved_address_1 = require("./use-is-saved-address");
const use_list_customer_addresses_1 = require("./use-list-customer-addresses");
const use_payment_account_address_1 = require("./use-payment-account-address");
/**
 * This hook can be used to help set up a billing address form state including fetching the
 * customer's saved address (if authenticated) and managing the address state.
 */
const useAddressForm = (props) => {
    const { authState, cartState, customerClient, previewOptions, selectedPayment, selectedSavedPaymentMethod } = props;
    const { isAuthenticating } = authState;
    const { cart, resolving } = cartState;
    const { addresses: savedAddresses, isFetching: loadingAddresses } = (0, use_list_customer_addresses_1.useListCustomerAddresses)({
        authState,
        customerClient,
        previewOptions
    });
    const addressOnCart = (0, exports.useAddressOnCart)(cartState);
    const defaultUserAddress = (0, exports.useUserDefaultAddress)(savedAddresses);
    let selectedPaymentAddress = (0, use_payment_account_address_1.usePaymentAccountAddress)(selectedPayment);
    if (selectedPaymentAddress) {
        console.warn('PaymentAccount was used to obtain billing address, SavedPayment should be used instead.');
    }
    else {
        selectedPaymentAddress = selectedSavedPaymentMethod === null || selectedSavedPaymentMethod === void 0 ? void 0 : selectedSavedPaymentMethod.billingAddress;
    }
    const [selectedAddress, setSelectedAddress] = (0, react_1.useState)(selectedPaymentAddress ||
        addressOnCart ||
        defaultUserAddress ||
        payment_js_1.EmptyAddress);
    const usingSavedAddress = (0, use_is_saved_address_1.useIsSavedAddress)({
        savedAddresses,
        selectedAddress,
        savedPaymentAddress: selectedPaymentAddress
    });
    (0, react_1.useEffect)(() => {
        if (!cart || resolving || isAuthenticating || loadingAddresses) {
            return;
        }
        if (!(0, lodash_1.isEmpty)(selectedPaymentAddress)) {
            setSelectedAddress(selectedPaymentAddress);
            return;
        }
        if (!(0, lodash_1.isEmpty)(addressOnCart)) {
            setSelectedAddress(addressOnCart);
            return;
        }
        if (!(0, lodash_1.isEmpty)(defaultUserAddress)) {
            setSelectedAddress(defaultUserAddress);
            return;
        }
        setSelectedAddress(payment_js_1.EmptyAddress);
    }, [
        cart,
        defaultUserAddress,
        addressOnCart,
        isAuthenticating,
        loadingAddresses,
        resolving,
        selectedPaymentAddress
    ]);
    return (0, react_1.useMemo)(() => ({
        savedAddresses,
        selectedAddress,
        setSelectedAddress,
        usingSavedAddress
    }), [savedAddresses, selectedAddress, usingSavedAddress]);
};
exports.useAddressForm = useAddressForm;
const useAddressOnCart = (cartState) => {
    const { cart, resolving } = cartState;
    const [address, setAddress] = (0, react_1.useState)();
    (0, react_1.useEffect)(() => {
        var _a;
        if (!cart || resolving) {
            return;
        }
        const fulfillment = (_a = cart === null || cart === void 0 ? void 0 : cart.fulfillmentGroups[0]) === null || _a === void 0 ? void 0 : _a.address;
        setAddress((0, lodash_1.get)(cart, 'activePayments[0].billingAddress', fulfillment));
    }, [cart, resolving]);
    return address;
};
exports.useAddressOnCart = useAddressOnCart;
const useUserDefaultAddress = (addresses) => {
    const [address, setAddress] = (0, react_1.useState)();
    (0, react_1.useEffect)(() => {
        if ((0, lodash_1.isEmpty)(addresses)) {
            return;
        }
        const cartProp = 'defaultBillingAddress';
        setAddress((0, lodash_1.find)(addresses, [cartProp, true]) || (0, lodash_1.head)(addresses));
    }, [addresses]);
    return address;
};
exports.useUserDefaultAddress = useUserDefaultAddress;
//# sourceMappingURL=use-address-form.js.map